<template>
  <b-modal
    id="page-document-card-modal"
    :ok-title="trans(block && block.id ? 'save' : 'create')"
    :ok-disabled="!media_id"
    :cancel-title="trans('cancel')"
    size="lg"
    no-enforce-focus
    @shown="onShown"
    @hidden="shown = false"
    @ok="onSave"
  >
    <template v-slot:modal-title>
      <span>{{ trans('document') }} (PDF)</span>
      <small v-if="!!block" class="text-muted ml-2">
        #{{ block.id }}
      </small>
    </template>

    <b-row>
      <b-col class="col-12 col-lg-7">
        <b-tabs v-model="tab" content-class="mt-3">
          <b-tab :title="trans('document')">
            <b-form-group :label="trans('choose_a_document')">
              <document-dropdown
                v-if="shown"
                :organization="organization"
                v-model="media_id"
                :initial_short_url="button_url"
                :placeholder="trans('choose_a_document')"
                @document="onSelectDocument"
                :disabled="busy"
              ></document-dropdown>
              <div class="text-center"><em>{{ trans('or') }}</em></div>
              <b-form-group :label="trans('upload_pdf')">
                <b-form-file
                  v-model="upload_file"
                  :placeholder="trans('upload_pdf')"
                  :drop-placeholder="trans('drop_file_here')"
                  accept="application/pdf"
                  :disabled="busy"
                  @input="onDocumentFileChange(null)"
                />
                <b-form-text>
                  {{ trans('documents_can_be_managed') }}
                </b-form-text>
              </b-form-group>
            </b-form-group>

            <p v-if="busy">
              <i class="fa fa-fw fa-circle-notch fa-spin"></i>
              {{ trans('please_wait') }}
            </p>
            <p v-if="error" class="text-danger">
              {{ error }}
            </p>

            <b-form-group :label="trans('button')">
              <b-form-input
                v-model="button_text"
                :disabled="busy"
              />
            </b-form-group>

            <b-form-group :label="trans('anchor_target')">
              <b-form-select
                v-model="button_target"
                :options="button_target_options"
                :disabled="busy"
              ></b-form-select>
            </b-form-group>

            <b-form-group :label="trans('width')">
              <b-form-radio-group v-model="image_percent" :options="image_percent_options" />
            </b-form-group>
            
            <b-form-group :label="trans('position')">
              <b-form-radio-group v-model="span_row">
                <b-form-radio :value="false">
                  {{ trans('inline') }}
                </b-form-radio>
                <b-form-radio :value="true">
                  {{ trans('span_row') }}
                </b-form-radio>
              </b-form-radio-group>
            </b-form-group>
            <b-form-group v-if="span_row" :label="trans('align')">
              <b-form-radio-group v-model="h_align" :options="hAlignOptions"></b-form-radio-group>
            </b-form-group>
          </b-tab>
          <b-tab :title="trans('text')">
            <b-form-group>
              <html-textarea
                :organization="organization"
                v-model="text"
                for-microsite
                show-insert-form-document
              ></html-textarea>
            </b-form-group>
          </b-tab>
        </b-tabs>
      </b-col>
      <b-col class="col-6 col-lg-5">
        <page-card-block
          :block="serializedForPreview"
        ></page-card-block>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
  import DocumentDropdown from './../media/DocumentDropdown'
  import HtmlTextarea from './../../../common/components/HtmlTextarea'
  import PageCardBlock from './PageCardBlock'

  export default {
    props: {
      organization: Object,
      page: Object,
      block: Object,
      after: Number,
    },
    components: {
      DocumentDropdown,
      HtmlTextarea,
      PageCardBlock,
    },
    data() {
      return {
        trans: trans,
        busy: false,
        error: null,
        shown: false,
        tab: 0,
        text: null,
        button_text: trans('open') + ' (PDF)',
        button_url: null,
        button_target: '_blank',
        button_target_options: [
          {text: trans('anchor_target_blank'), value: '_blank'},
          {text: trans('anchor_target_self'), value: null}, // self
        ],
        image_percent: 0.3333,
        image_percent_options: [
          {text: '25%', value: 0.25},
          {text: '33%', value: 0.3333},
          {text: '50%', value: 0.5},
          {text: '66%', value: 0.6666},
          {text: '75%', value: 0.75},
          {text: '100%', value: 1.0},
        ],
        span_row: false,
        h_align: 'center',
        hAlignOptions: [
          {value: 'left', text: trans('left')},
          {value: 'center', text: trans('center')},
          {value: 'right', text: trans('right')},
        ],
        media_id: null, // document
        document: null,
        upload_file: null,
      }
    },
    computed: {
      serialized() {
        return {
          type: 'document-card',
          text: this.text,
          button_text: this.button_text,
          button_url: this.button_url,
          button_target: this.button_target,
          media_id: this.media_id,
          image_percent: this.image_percent,
          span_row: this.span_row,
          h_align: this.h_align,
          after: this.after,
        }
      },
      serializedForPreview() {
        return _.assign({}, this.serialized, {span_row: false})
      },
    },
    methods: {
      onDocumentFileChange() {
        if (!this.upload_file) return
        const url = `/organization/${this.organization.id}/media/document`
        this.busy = true
        this.error = null
        let formData = new FormData();
        formData.append("file", this.upload_file);
        axios.post(url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then(response => {
          this.busy = false
          this.media_id = response.data.id
          this.text = `<h5 style="text-align:center;">${response.data.name}</h5>`
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onSelectDocument(doc, context) {
        this.document = doc
        this.button_url = doc ? doc.short_url : null
        if (context && context.source == 'user') {
          this.text = `<h5 style="text-align:center;">${doc.name}</h5>`
        }
      },
      onShown() {
        this.busy = false
        this.error = null
        this.text = this.block ? this.block.text : null
        this.button_text = this.block ? this.block.button_text : (trans('open') + ' (PDF)')
        this.button_url = this.block ? this.button_url : null
        this.button_target = this.block ? this.block.button_target : '_blank'
        this.media_id = this.block ? this.block.media_id : null
        this.image_percent = this.block ? this.block.image_percent : 0.3333
        this.span_row = this.block ? this.block.span_row || false : false
        this.h_align = this.block ? this.block.h_align || 'center' : 'center'
        this.shown = true
      },
      onSave(e) {
        e.preventDefault()
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/page/${this.page.id}/block`
        if (this.block && this.block.id) url += `/${this.block.id}`
        axios[this.block && this.block.id ? 'patch' : 'post'](url, this.serialized)
        .then(response => {
          this.busy = false
          this.$emit('saved', response.data)
          this.$bvModal.hide('page-document-card-modal')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
    },
    mounted() {
      window.pageDocumentCardModal = this
    },
  }
</script>